import { ArrowDownRight, ArrowUpRight, Clock, Home, LogOut, PawPrint } from "lucide-react";

import type { Kerncijfer } from "../api/insights";

/**
 * De vier kaarten boven het scherm: instroom, uitstroom, verblijfsduur en bezetting.
 *
 * <p><b>De kleur van het verschil hangt aan het cijfer en niet aan het teken.</b> Een pijl omhoog is
 * niet vanzelf goed nieuws: meer instroom betekent meer dieren die een plek nodig hebben, en een
 * kortere verblijfsduur betekent sneller een nieuw huis. De server zegt per cijfer welke kant het
 * gunstige op ligt; dit onderdeel kleurt op basis daarvan en verzint er niets bij.
 *
 * <p>Bij uitstroom staat er geen kleur. Daar zitten plaatsingen én euthanasie in één getal, en die
 * kant kiezen zou het scherm iets laten beweren wat het niet weet. Geen kleur is dan eerlijker dan
 * een kleur die de helft van de gevallen verkeerd heeft.
 */

/** Een icoon per cijfer, op sleutel. Een onbekende sleutel krijgt het pootje. */
const ICONEN: Record<string, React.ReactNode> = {
  binnengekomen: <PawPrint />,
  vertrokken: <LogOut />,
  verblijfsduur: <Clock />,
  aanwezig: <Home />,
};

/**
 * Of dit verschil gunstig is.
 *
 * <p>Null betekent: daar valt niets over te zeggen. Dat is niet hetzelfde als "neutraal uitgevallen"
 * — een verschil van nul procent is neutraal, maar over een verschil in uitstroom valt ook bij tien
 * procent geen oordeel te geven.
 */
export function isGunstig(procent: number, richting: Kerncijfer["richting"]): boolean | null {
  if (richting === "GEEN" || procent === 0) return null;
  return richting === "HOGER_BETER" ? procent > 0 : procent < 0;
}

export function Kerncijfers({ cijfers, t }: {
  cijfers: Kerncijfer[];
  t: (key: string) => string;
}) {
  if (cijfers.length === 0) return null;

  const heel = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 });
  const komma = new Intl.NumberFormat("nl-NL",
    { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // Een voorbehoud dat bij een percentage hoort — bijvoorbeeld dat de gekozen periode langer is
  // dan een jaar en dus met zichzelf overlapt — geldt voor alle kaarten tegelijk. Het in elke
  // kaart zetten leverde drie keer dezelfde alinea op, die de kaarten uit vorm duwde en het cijfer
  // wegdrukte waar iemand voor kwam. Eén regel onder de rij, en alleen de verschillende.
  const voorbehouden = [...new Set(cijfers
    .filter((c) => c.procent != null && c.toelichting)
    .map((c) => c.toelichting as string))];

  return (
    <>
    <div className="fawkes-di__kengetallen">
      {cijfers.map((c) => {
        const gunstig = c.procent == null ? null : isGunstig(c.procent, c.richting);
        // Dagen krijgen een decimaal, dieren niet: een half dier bestaat niet, en 42,0 dagen
        // afronden naar 42 maakt van een verschil van een halve dag niets.
        const getal = c.eenheid === "dagen" ? komma : heel;
        return (
          <div className="fawkes-di__kengetal" key={c.sleutel}>
            <span className="fawkes-di__kengetal-icoon" aria-hidden="true">
              {ICONEN[c.sleutel] ?? <PawPrint />}
            </span>
            <div className="fawkes-di__kengetal-tekst">
              <span className="fawkes-di__kengetal-naam">{c.naam}</span>
              {/* Geen cijfer is geen nul. Nul beweert dat er geteld is en dat het niets was. */}
              <strong className="fawkes-di__kengetal-getal">
                {c.waarde == null ? "—" : getal.format(c.waarde)}
                {c.waarde != null && c.eenheid === "dagen"
                  ? <span className="fawkes-di__kengetal-eenheid"> {c.eenheid}</span>
                  : null}
              </strong>
              {c.procent != null ? (
                <span
                  className={"fawkes-di__kengetal-sub"
                    + (gunstig === true ? " fawkes-di__kengetal-sub--goed" : "")
                    + (gunstig === false ? " fawkes-di__kengetal-sub--slecht" : "")}
                >
                  <span className="fawkes-di__kengetal-pijl" aria-hidden="true">
                    {c.procent > 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                  </span>
                  {c.procent > 0 ? "+" : ""}{komma.format(c.procent)}%
                  {" "}{t("dataInsight.tovVorigJaar")}
                </span>
              ) : (
                // Waarom er geen vergelijking staat. Een lege plek laat iemand denken dat er iets
                // niet geladen is; dit zegt dat er niets te vergelijken viel.
                <span className="fawkes-di__kengetal-sub">
                  {c.toelichting ?? t("dataInsight.geenVergelijking")}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
    {voorbehouden.length > 0 ? (
      <p className="fawkes-di__kengetal-voorbehoud">{voorbehouden.join(" ")}</p>
    ) : null}
    </>
  );
}
